import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { Component, inject, input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';

import { TooltipComponent } from '../tooltip/tooltip.component';

@Component({
  selector: 'lib-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    TooltipComponent,
    FeatherModule,
  ],
})
export class SelectComponent implements OnChanges, OnDestroy, ControlValueAccessor {
  private translate = inject(TranslateService);

  readonly id = input(`select-${Math.random().toString(36).substring(2, 8)}`);
  readonly label = input('');
  readonly options = input<
    {
      value: string;
      label: string;
    }[]
  >([]);
  readonly placeholder = input('');

  get computedPlaceholder(): string {
    // For multi-select, hide placeholder if there are selected values or user is searching
    if (this.multiple()) {
      const currentVal = this.currentValue;
      if ((Array.isArray(currentVal) && currentVal.length > 0) || this.isSearching) {
        return '';
      }
    }

    return this.placeholder() || this.translate.instant('select-an-option');
  }
  readonly required = input<boolean>();
  readonly multiple = input(false);
  readonly clearable = input(true);
  readonly disabled = input(false);
  readonly readOnly = input(false);
  readonly tooltip = input<string>();
  // Track disabled state from Angular forms API
  isDisabled = false;
  readonly value = input<string | null | undefined | string[]>(null);

  filteredOptions: { value: string; label: string }[] = [];
  ngControl: NgControl | null = inject(NgControl, { self: true, optional: true });
  internalValue: string | string[] | null = null;
  isSearching = false;
  private searchSubject$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Callbacks for ControlValueAccessor interface
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: string | string[] | null) => {
    /* Implemented by Angular forms */
  };
  onTouched = () => {
    /* Implemented by Angular forms */
  };

  constructor() {
    if (this.ngControl != null) {
      // Assigning this component as the value accessor for the control
      this.ngControl.valueAccessor = this;
    }

    // Set up debounced search
    this.searchSubject$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.isSearching = searchTerm.length > 0;
        // Additional search logic can be added here if needed in the future
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      // When options change, update filteredOptions
      this.filteredOptions = [...this.options()];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject$.complete();
  }

  // Optional helper methods for programmatic control of ng-select
  openSelect(select: NgSelectComponent): void {
    select.open();
  }

  closeSelect(select: NgSelectComponent): void {
    select.close();
  }

  selectOptionsRange(from: number, to: number): void {
    this.filteredOptions = this.options().slice(from, to);
  }

  // --- ControlValueAccessor Interface Implementation ---
  writeValue(value: string | string[] | null): void {
    this.internalValue = value;
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // Handles change events from ng-select.
  handleChange(event: string | string[] | null): void {
    this.internalValue = event;
    this.onChange(this.internalValue);
    this.onTouched();
  }

  // Handle search events
  onSearch(searchTerm: string): void {
    // Emit to the debounced subject instead of directly setting state
    this.searchSubject$.next(searchTerm);
  }

  onOpen(): void {
    // Reset search state when opening
    this.isSearching = false;
  }

  onClose(): void {
    // Reset search state when closing
    this.isSearching = false;
  }

  get currentValue(): string | string[] | null {
    // Use external value prop if provided, otherwise use internal value from forms
    const externalValue = this.value();
    if (externalValue !== null && externalValue !== undefined && externalValue !== '') {
      return externalValue;
    }

    // Return internal value as-is, let ng-select handle empty values
    return this.internalValue;
  }
  // --- End of ControlValueAccessor Implementation ---

  get isRequired(): boolean {
    // First check explicit required input
    if (this.required()) {
      return true;
    }

    // Then check if form control has required validator
    const control = this.ngControl?.control;
    if (!control) {
      return false;
    }

    // Check if the control has a required validator
    const validator = control.validator;
    if (!validator) {
      return false;
    }

    // Run validator with empty value to check for required error
    const validationResult = validator({ value: null } as AbstractControl);
    return validationResult?.['required'] === true;
  }

  // Utility getter to decide when to show error messages.
  get showError(): boolean {
    const control = this.ngControl?.control;
    if (!control || !control.invalid) return false;

    // Show errors immediately for populated fields, or after user interaction for empty fields
    const hasValue =
      control.value != null &&
      control.value !== '' &&
      (!Array.isArray(control.value) || control.value.length > 0);
    return hasValue || control.dirty || control.touched;
  }

  get formControl(): FormControl {
    return this.ngControl?.control as FormControl;
  }

  // Returns a simple error message based on validations.
  getErrorMessage(): string {
    const control = this.ngControl?.control;
    if (control?.errors?.['required']) {
      return 'This field is required';
    }
    return 'Invalid selection';
  }
}
